import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import {
  Zap, BarChart3, Calendar, Users, DollarSign, UserCircle, Clock,
  Gift, Crown, Bell, Package, CreditCard, FileText, Settings,
  Home, Scissors, LogOut, Wrench, ChevronLeft, ChevronRight, MoreHorizontal, Globe, TrendingUp, UserPlus, Building2, LayoutDashboard, Target, Megaphone, PieChart, Activity, Shield,
  PawPrint, Dumbbell, ShoppingBag, Stethoscope, Camera, Send, Upload,
} from 'lucide-react';
import './Layout.css';

// ── Business type → which sidebar sections/items to show ────────────────────
// Types that show the "Shop" section (work orders, vehicles, estimates, etc.)
var SHOP_TYPES = ['auto', 'motorcycle', 'auto_repair', 'bike_shop', 'mechanic'];

// Types that show "Bookings" (service-based businesses)
var BOOKING_TYPES = ['spa', 'motorcycle', 'motorcycle_repair', 'auto', 'auto_repair', 'automotive', 'mechanic', 'massage', 'salon', 'barbershop', 'barber', 'nails', 'nail_salon',
  'hair_salon', 'medspa', 'med_spa', 'beauty', 'pet', 'pet_grooming', 'grooming',
  'dog_grooming', 'fitness', 'gym', 'yoga', 'studio', 'crossfit', 'martial_arts', 'other'];

// Types that show inventory
var INVENTORY_TYPES = ['smokeshop', 'smoke_shop', 'smoke', 'vape', 'dispensary',
  'auto', 'motorcycle', 'auto_repair', 'barbershop', 'barber', 'nails', 'nail_salon',
  'pet', 'pet_grooming', 'medspa', 'med_spa', 'spa', 'massage', 'salon', 'beauty'];

// ── Section label overrides by business type ────────────────────────────────
function getSectionLabels(type) {
  var t = (type || '').toLowerCase();
  if (SHOP_TYPES.includes(t)) {
    return { main: 'Main', team: 'Team', grow: 'Grow', shop: 'Shop', account: 'Account' };
  }
  if (['smokeshop', 'smoke_shop', 'smoke', 'vape', 'dispensary'].includes(t)) {
    return { main: 'Main', team: 'Team', grow: 'Grow', shop: 'Store', account: 'Account' };
  }
  if (['fitness', 'gym', 'yoga', 'studio', 'crossfit', 'martial_arts'].includes(t)) {
    return { main: 'Main', team: 'Team', grow: 'Grow', shop: null, account: 'Account' };
  }
  // Default for spa, barbershop, pet grooming, etc. — no shop section
  return { main: 'Main', team: 'Team', grow: 'Grow', shop: null, account: 'Account' };
}

function Layout({ children }) {
  var location = useLocation();
  var { signOut } = useAuth();
  var { businessName, businessType, isSuperAdmin, tenant } = useTenant();
  var [collapsed, setCollapsed] = useState(false);
  var [mobileMore, setMobileMore] = useState(false);

  var bt = (businessType || '').toLowerCase();
  var isShopType = SHOP_TYPES.includes(bt);
  var isBookingType = BOOKING_TYPES.includes(bt) || !isShopType;
  var hasInventory = INVENTORY_TYPES.includes(bt) || isShopType;
  var labels = getSectionLabels(bt);

  var sections = [];

  var overrideId = null;
  try { overrideId = localStorage.getItem('sa_tenant_override'); } catch(e) {}
  if (isSuperAdmin && !overrideId) {
    // Super admin always sees everything
    sections = [
      { title: 'Platform', items: [
        { path: '/app/admin', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/app/admin/businesses', icon: Building2, label: 'All Businesses' },
        { path: '/app/admin/sites', icon: Globe, label: 'Client Websites' },
        { path: '/app/admin/revenue', icon: TrendingUp, label: 'Revenue & MRR' },
      ]},
      { title: 'Sales', items: [
        { path: '/app/admin/leads', icon: UserPlus, label: 'Leads & Demos' },
        { path: '/app/admin/onboarding', icon: Target, label: 'Onboarding' },
        { path: '/app/admin/campaigns', icon: Megaphone, label: 'Outreach' },
      ]},
      { title: 'Operations', items: [
        { path: '/app/admin/activity', icon: Activity, label: 'Activity Feed' },
        { path: '/app/admin/analytics', icon: PieChart, label: 'Analytics' },
        { path: '/app/admin/settings', icon: Shield, label: 'Platform Settings' },
      ]},
    ];
    if (overrideId) sections.push({ title: '⬅ Back to Admin', items: [
        { path: '/app/admin', icon: Shield, label: 'Exit Tenant View', onClick: function() { localStorage.removeItem('sa_tenant_override'); window.location.href = '/app/admin'; } },
      ]});
    if (overrideId) sections.push({ title: 'Tenant View', items: [
        { path: '/app', icon: BarChart3, label: 'Their Dashboard' },
        { path: '/app/bookings', icon: Calendar, label: 'Bookings' },
        { path: '/app/customers', icon: Users, label: 'Customers' },
        { path: '/app/services', icon: Scissors, label: 'Services' },
        { path: '/app/staff', icon: UserCircle, label: 'Staff' },
        { path: '/app/work-orders', icon: Wrench, label: 'Work Orders' },
        { path: '/app/vehicles', icon: Package, label: 'Vehicles' },
        { path: '/app/inventory', icon: Package, label: 'Inventory' },
        { path: '/app/vendors', icon: ShoppingBag, label: 'Vendors' },
        { path: '/app/purchase-orders', icon: Package, label: 'Purchase Orders' },
        { path: '/app/finance', icon: Package, label: 'Finance' },
        { path: '/app/invoices', icon: CreditCard, label: 'Invoices' },
        { path: '/app/site', icon: Globe, label: 'Their Website' },
        { path: '/app/business-settings', icon: Settings, label: 'Their Settings' },
      ]});
    
  } else {
    // ── Back to Admin (when overriding) ──────────────────────
    if (isSuperAdmin && overrideId) {
      sections.push({ title: '⬅ Admin', items: [
        { path: '/app/admin', icon: Shield, label: 'Exit Tenant View', onClick: function() { localStorage.removeItem('sa_tenant_override'); window.location.href = '/app/admin'; } },
      ]});
    }

    var isSmokeShop = ['smokeshop', 'smoke_shop', 'smoke', 'vape', 'dispensary'].includes(bt);
    var isRental = bt === 'rental';
    console.log("LAYOUT bt:", bt, "isSmokeShop:", isSmokeShop);

    if (isRental) {
      sections.push({ title: 'MAIN', items: [
        { path: '/app?view=dashboard', icon: Home, label: 'Dashboard' },
        { path: '/app?view=calendar', icon: Calendar, label: 'Calendar' },
        { path: '/app?view=orders', icon: Package, label: 'Orders' },
        { path: '/app?view=inventory', icon: ShoppingBag, label: 'Inventory' },
        { path: '/app?view=documents', icon: FileText, label: 'Documents' },
        { path: '/app?view=clients', icon: Users, label: 'Clients' },
        { path: '/app?view=reports', icon: BarChart3, label: 'Reports' },
      ]});

    } else if (!isSmokeShop) {
    // ── Main section ──────────────────────────────────────────
    var mainItems = [
      { path: '/app', icon: Home, label: 'Dashboard' },
    ];

    if (isBookingType) {
      mainItems.push({ path: '/app/bookings', icon: Calendar, label: isShopType ? 'Appointments' : 'Bookings' });
    }

    mainItems.push({ path: '/app/customers', icon: Users, label: 'Customers' });

    if (isBookingType) {
      mainItems.push({ path: '/app/services', icon: Scissors, label: 'Services' });
    }

    mainItems.push({ path: '/app/checkout', icon: DollarSign, label: 'New Sale' });

    sections.push({ title: labels.main, items: mainItems });

    // ── Team section ──────────────────────────────────────────
    sections.push({ title: labels.team, items: [
      { path: '/app/staff', icon: UserCircle, label: 'Staff' },
      { path: '/app/schedule', icon: Clock, label: 'Schedule' },
      { path: '/app/commissions', icon: DollarSign, label: 'Commissions' },
      { path: '/app/reports', icon: BarChart3, label: 'Reports' },
    ]});

    // ── Business section (spa, salon, etc) ───────────────────
    if (['spa', 'massage', 'salon', 'barbershop', 'barber', 'nails', 'nail_salon',
         'hair_salon', 'beauty', 'medspa', 'med_spa'].includes(bt)) {
      sections.push({ title: 'Business', items: [
        { path: '/app/vendors', icon: ShoppingBag, label: 'Vendors' },
        { path: '/app/purchase-orders', icon: Package, label: 'Purchase Orders' },
        { path: '/app/finance', icon: DollarSign, label: 'Finance' },
        { path: '/app/treatment-notes', icon: FileText, label: 'Treatment Notes' },
      ]});
    }
    } // end !isSmokeShop

    // ── Grow section ──────────────────────────────────────────
    if (!isSmokeShop && !isRental) {
    var growItems = [
      { path: '/app/gift-cards', icon: Gift, label: 'Gift Cards' },
      { path: '/app/memberships', icon: Crown, label: 'Memberships' },
      { path: '/app/reminders', icon: Bell, label: 'Reminders' },
    ];

    // Add SMS Campaigns & Notifications for all types
    growItems.push({ path: '/app/sms-campaigns', icon: Send, label: 'SMS Campaigns' });
    growItems.push({ path: '/app/notifications', icon: Bell, label: 'Notifications' });
    growItems.push({ path: '/app/import', icon: Upload, label: 'Import CSV' });

    // Add Client Photos for visual businesses
    if (['spa', 'massage', 'salon', 'barbershop', 'barber', 'nails', 'nail_salon',
         'hair_salon', 'beauty', 'pet', 'pet_grooming', 'grooming', 'dog_grooming',
         'medspa', 'med_spa'].includes(bt)) {
      growItems.push({ path: '/app/photos', icon: Camera, label: 'Client Photos' });
    }

    sections.push({ title: labels.grow, items: growItems });
    } // end !isSmokeShop && !isRental grow

    // ── Shop section (only for auto/motorcycle/smoke shop types) ──
    if (isShopType) {
      sections.push({ title: labels.shop || 'Shop', items: [
        { path: '/app/work-orders', icon: Wrench, label: 'Work Orders' },
        { path: '/app/vehicles', icon: Package, label: 'Vehicles' },
        { path: '/app/estimates', icon: FileText, label: 'Estimates' },
        { path: '/app/invoices', icon: FileText, label: 'Invoices' },
        { path: '/app/inventory', icon: Package, label: 'Inventory' },
        { path: '/app/vendors', icon: ShoppingBag, label: 'Vendors' },
        { path: '/app/purchase-orders', icon: Package, label: 'Purchase Orders' },
        { path: '/app/finance', icon: Package, label: 'Finance' },
      ]});
    } else if (isSmokeShop) {
      // Smoke shops get full custom sidebar
      sections.push({ title: 'Sales', items: [
        { path: '/app', icon: Home, label: 'Dashboard' },
        { path: '/app', icon: DollarSign, label: 'New Sale', onClick: () => window.dispatchEvent(new Event('openSmokeshopPOS')) },
        { path: '/app/customers', icon: Users, label: 'Customers' },
        { path: '/app/sales-history', icon: CreditCard, label: 'Transactions' },
      ]});
      sections.push({ title: 'Team', items: [
        { path: '/app/staff', icon: UserCircle, label: 'Staff' },
        { path: '/app/schedule', icon: Clock, label: 'Schedule' },
      { path: '/app/commissions', icon: DollarSign, label: 'Commissions' },
      { path: '/app/reports', icon: BarChart3, label: 'Reports' },
      ]});
      sections.push({ title: 'Store', items: [
        { path: '/app/inventory', icon: Package, label: 'All Products' },
        { path: '/app/inventory?cat=Vapes', icon: Package, label: '💨 Vapes' },
        { path: '/app/inventory?cat=Glass', icon: Package, label: '🔮 Glass & Pipes' },
        { path: '/app/inventory?cat=CBD', icon: Package, label: '🌱 CBD' },
        { path: '/app/inventory?cat=Papers', icon: Package, label: '📜 Papers & Wraps' },
        { path: '/app/inventory?cat=Hookah', icon: Package, label: '🪔 Hookah' },
        { path: '/app/inventory?cat=Misc', icon: Package, label: '🛍️ Accessories' },
      ]});
      sections.push({ title: 'Vendors & B2B', items: [
        { path: '/app/vendors', icon: ShoppingBag, label: 'Vendors' },
        { path: '/app/contacts', icon: Users, label: 'Business Contacts' },
        { path: '/app/crm', icon: TrendingUp, label: 'B2B Pipeline' },
      ]});
      sections.push({ title: 'Tools', items: [
        { path: '/app/import', icon: Upload, label: 'Import CSV' },
      ]});
      sections.push({ title: 'Marketing', items: [
        { path: '/app/sms-campaigns', icon: Send, label: 'SMS Campaigns' },
        { path: '/app/gift-cards', icon: Gift, label: 'Gift Cards' },
        { path: '/app/memberships', icon: Crown, label: 'Memberships' },
      ]});
    } else if (hasInventory) {
      // Some service businesses get just inventory (no full shop section)
      // Add it as a standalone item in a small section
      sections.push({ title: 'Inventory', items: [
        { path: '/app/inventory', icon: Package, label: 'Inventory' },
        { path: '/app/vendors', icon: ShoppingBag, label: 'Vendors' },
        { path: '/app/purchase-orders', icon: Package, label: 'Purchase Orders' },
        { path: '/app/finance', icon: Package, label: 'Finance' },
      ]});
    }

    if (!isRental) {
    // ── Account section ───────────────────────────────────────
    sections.push({ title: labels.account, items: [
      { path: '/app/business-settings', icon: Settings, label: 'Settings' },

      { path: '/app/site', icon: FileText, label: '🌐 My Website', onClick: function() {
        var slug = tenant?.custom_slug || tenant?.subdomain;
        if (!slug) { alert('No website URL configured for this business. Set a custom slug in Site Settings.'); return; }
        window.open('/site/' + slug, '_blank');
      } },
      { path: '/app/billing', icon: CreditCard, label: 'Billing' },
      { path: '/app/plan', icon: FileText, label: 'My Plan' },
    ]});
    } else {
    sections.push({ title: 'ACCOUNT', items: [
      { path: '/app/business-settings', icon: Settings, label: 'Settings' },
      { path: '/app/billing', icon: CreditCard, label: 'Billing' },
    ]});
    }
  }

  var bottomTabs = isSuperAdmin
    ? [
        { path: '/app/admin', icon: Zap, label: 'Center' },
        { path: '/app/bookings', icon: Calendar, label: 'Bookings' },
        { path: '/app/customers', icon: Users, label: 'Clients' },
        { path: '/app/checkout', icon: DollarSign, label: 'New Sale' },
      ]
    : [
        { path: '/app', icon: Home, label: 'Home' },
        { path: '/app/bookings', icon: Calendar, label: 'Bookings' },
        { path: '/app/customers', icon: Users, label: 'Clients' },
        { path: '/app/checkout', icon: DollarSign, label: 'New Sale' },
      ];

  var allItems = [];
  sections.forEach(function(sec) { sec.items.forEach(function(item) { allItems.push(item); }); });
  var moreItems = allItems.filter(function(item) {
    return !bottomTabs.some(function(t) { return t.path === item.path; });
  });

  var isActive = function(path) {
    if (path === '/app') return location.pathname === '/app' || location.pathname === '/app/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="layout">
      {overrideId && isSuperAdmin && (
        <div style={{ background: "linear-gradient(90deg, #ff6b2b, #ff8f5a)", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }}>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>⚠️ Viewing as: {businessName} (Tenant #{overrideId})</span>
          <button onClick={function() { localStorage.removeItem("sa_tenant_override"); window.location.href = "/app/admin"; }} style={{ background: "#fff", color: "#ff6b2b", border: "none", borderRadius: "6px", padding: "6px 16px", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>← Back to Admin</button>
        </div>
      )}
      <aside className={"sidebar" + (collapsed ? " sidebar-collapsed" : "")} style={{...(overrideId && isSuperAdmin ? { paddingTop: "50px" } : {}), ...(isRental ? { display: "none" } : {})}}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon"><span>iV</span></div>
          {!collapsed && (
            <div className="sidebar-logo-info">
              <div className="sidebar-logo-title">IVision Marketing</div>
              <div className="sidebar-logo-sub">{businessName}</div>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {sections.map(function(sec, i) {
            return (
              <div key={i} className="sidebar-section">
                {!collapsed && <div className="sidebar-section-title">{sec.title}</div>}
                {sec.items.map(function(item) {
                  var IconComp = item.icon;
                  return (
                    <Link key={item.path} to={item.path}
                      className={'sidebar-item' + (isActive(item.path) ? ' active' : '')}
                      title={collapsed ? item.label : ''}
                      onClick={item.onClick ? function(e) { e.preventDefault(); item.onClick(); } : undefined}>
                      <span className="sidebar-item-icon"><IconComp size={18} strokeWidth={1.8} /></span>
                      {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-bottom">
          <button className="sidebar-collapse-btn" onClick={function() { setCollapsed(!collapsed); }}>
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          <button className="sidebar-signout" style={{ opacity: 0.5 }} onClick={signOut}>
            <span><LogOut size={18} strokeWidth={1.8} /></span>
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <header className="mobile-header">
        <div className="mobile-header-left">
          <div className="sidebar-logo-icon small"><span>iV</span></div>
          <div>
            <div className="mobile-title">IVision Marketing</div>
            <div className="mobile-sub">{businessName}</div>
          </div>
        </div>
        <button className="mobile-signout" onClick={signOut}>Sign Out</button>
      </header>

      <main className={'layout-content' + (collapsed ? ' sidebar-is-collapsed' : '')} style={isRental ? {marginLeft:0} : {}}>
        {children}
      </main>

      <nav className="mobile-bottom">
        {bottomTabs.map(function(item) {
          var IconComp = item.icon;
          return (
            <Link key={item.path} to={item.path}
              className={'mobile-tab' + (isActive(item.path) ? ' active' : '')}
              onClick={function() { setMobileMore(false); }}>
              <span className="mobile-tab-icon"><IconComp size={20} strokeWidth={1.8} /></span>
              <span className="mobile-tab-label">{item.label}</span>
            </Link>
          );
        })}
        <button className={'mobile-tab' + (mobileMore ? ' active' : '')}
          onClick={function() { setMobileMore(!mobileMore); }}>
          <span className="mobile-tab-icon"><MoreHorizontal size={20} strokeWidth={1.8} /></span>
          <span className="mobile-tab-label">More</span>
        </button>
      </nav>

      {mobileMore && (
        <div className="mobile-more-overlay" onClick={function() { setMobileMore(false); }}>
          <div className="mobile-more-sheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="mobile-more-handle"></div>
            {moreItems.map(function(item) {
              var IconComp = item.icon;
              return (
                <Link key={item.path} to={item.path} className="mobile-more-item"
                  onClick={function() { setMobileMore(false); }}>
                  <span className="mobile-more-icon"><IconComp size={18} strokeWidth={1.8} /></span>
                  <span className="mobile-more-label">{item.label}</span>
                  <span className="mobile-more-arrow">{'\u203A'}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;
// cache bust Tue Feb 17 23:40:26 PST 2026
